import { Map as ImmutableMap } from 'immutable';
import { v4 as uuidv4 } from 'uuid';
import { mkUndo } from 'data/undo/undoTypes';
import * as undoActions from 'data/undo/undoActions';
import moment from 'moment';

import { initReductions, addReduction, clearReductions } from 'data/optimalCacheManager';
import * as transactionActions from './actions';


// import { getLogger } from 'utils/logger';
// const log = getLogger('data/transactions/reducerHelpers.js');

// These are recreated here to prevent dependency cycles, will sort it out later
//
function flattenTransactionsById(transactionsByAccountId) {
  return (new ImmutableMap()).withMutations(
    (flatTransactionsById) => transactionsByAccountId.forEach((transactionsById) => flatTransactionsById.merge(transactionsById))
  );
}
export function findTransaction(txnsByAccountId, txnId, accountId) {

  let ret = null;
  if (accountId) {
    const acctTxns = txnsByAccountId.get(accountId);
    if (acctTxns) {
      ret = acctTxns.get(txnId);
    }
  }
  if (!ret) {
    txnsByAccountId.some((txnList) =>
      txnList.some((tx) => {
        if (tx.id === txnId) {
          ret = tx;
          return true;
        }
        return false;
      }));
  }
  return ret;
}


// KEN COMMENTS
// So here is the update flow I was trying to achieve:
// - If a transaction changes, then that will cause the overall top-level store to change (transactionsByAccountId)
// - However, if holding reference to the transactions within an accountID then that will only change if txns in the account change
// - So, in Register View, it will cause it to re-render if the 2nd level map changes
// - But, each transaction row shouldn't have it's render called unless the txn changed
// - This is supposed to be handled through a combination of things,
//   - React knows the row because it should be based on txn id so it can find even if list changed
// - The transaction itself which is passed down as prop shouldnt have changed even though the parent map may have changed


// upsertTxn
//
// Optimized to upsert a single transaction into the transaction store. By
// default, calling this function will also terminate the loading state unless
// called with the loadPending argument set to true.
//
// Returns the updated state.
//
export function upsertTxn(state, txnToUpsertOriginal, lastSyncDate) {

  let newState = state;
  // We add a unix timestamp for faster sorting to every inserted transaction to redux
  const txnToUpsert = txnToUpsertOriginal.set('postedOnAsUnix', convertToUnixDay(txnToUpsertOriginal.postedOn));

  //
  // WE check here to see if the transaction changed accounts, by ensuring this transaction ID only
  // exists ONCE in our local store.  If it exists elsewhere in Redux, we delete it
  //
  const oldTxn = findTransaction(newState.transactionsByAccountId, txnToUpsert.id, txnToUpsert.accountId);
  if (oldTxn && oldTxn.accountId !== txnToUpsert.accountId) {
    newState = upsertTxn(newState, oldTxn.set('isDeleted', true));
  }

  newState = initReductions(newState);

  let txnsByIdForAccountId = newState.transactionsByAccountId.get(txnToUpsert.accountId);
  if (!txnsByIdForAccountId) {
    // if we don't a map of txns by ID for this account, create it now
    txnsByIdForAccountId = new ImmutableMap();
  }

  if (!txnToUpsert.isDeleted) {
    txnsByIdForAccountId = txnsByIdForAccountId.set(txnToUpsert.id, txnToUpsert);
  } else {
    txnsByIdForAccountId = txnsByIdForAccountId.remove(txnToUpsert.id);
  }

  // update txnsByAccountId entry for the account of the upserted txn
  const txnsByAccountId = txnsByIdForAccountId.size > 0 ?
    newState.transactionsByAccountId.set(txnToUpsert.accountId, txnsByIdForAccountId) :
    newState.transactionsByAccountId.delete(txnToUpsert.accountId);

  // add to the reduction (optimalCacheManager)
  newState = addReduction(newState, oldTxn, txnToUpsert);

  return newState.merge({
    transactionsByAccountId: txnsByAccountId,
    loadPending: false,
    lastSyncDate: lastSyncDate || newState.lastSyncDate,
  });

}

// upsertTxns
//
// Optimized to upsert multiple transactions into the transaction store. By
// default, calling this function will also terminate the loading state unless
// called with the loadPending argument set to true.
//
export function upsertTxns(state, data) {
  const {
    resources: transactions,
    replace = true,
    loadPending = false,
    lastSyncDate,
  } = data;

  // optimize when upserting 0 or 1 txns
  //
  // log.debug('UPSERT: ==============================', transactions, transactions.length);
  if (transactions.length === 0) {
    return state.set('loadPending', loadPending);
  }
  if (transactions.length === 1) {
    return upsertTxn(state, transactions[0], lastSyncDate);
  }

  const flatTxnByIdList = flattenTransactionsById(state.transactionsByAccountId) || new ImmutableMap();
  // log.debug('UPSERT MULTIPLE: ==============================', flatTxnByIdList, state.transactionsByAccountId);

  const txnsToDelete = [];
  const txnsToUpsertByAccountId = transactions.reduce((map, txn) => {
    let txnsToUpsertForAccountId = map.get(txn.accountId);
    if (!txnsToUpsertForAccountId) {
      map.set(txn.accountId, txnsToUpsertForAccountId = []);
    }
    txnsToUpsertForAccountId.push(txn);
    //
    // at this point, we also check to see if the account id CHANGED for this transaction
    // and if so, we add it to the "to be deleted" list
    //
    const oldTxn = flatTxnByIdList.get(txn.id);
    if (oldTxn && oldTxn.accountId !== txn.accountId) {
      txnsToDelete.push(oldTxn.set('isDeleted', true));
    }

    return map;
  }, new Map());

  // console.log ('UPSERT MULTIPLE: ==================to delete============', txnsToDelete);

  let newState = state;
  if (txnsToDelete.length > 0) {
    newState = upsertTxns(newState, { resources: txnsToDelete, replace: true, loadPending: true, lastSyncDate });
  }

  // If the UPSERT is more than 10 transactions, then don't optimize, but force selectors to recalculate
  const doOptimize = (transactions.length <= 10);
  if (!doOptimize) newState = clearReductions(newState);

  // Initialize the reduction using optimalCacheManager
  newState = initReductions(newState);

  const transactionsByAccountId = newState.transactionsByAccountId.withMutations((txnsByAccountId) => {
    txnsToUpsertByAccountId.forEach((txns, accountId) => {
      let txnsByIdForAccountId = txnsByAccountId.get(accountId);
      if (!txnsByIdForAccountId) {
        txnsByIdForAccountId = new ImmutableMap();
      }
      txnsByIdForAccountId = txnsByIdForAccountId.withMutations((txnsById) => {
        txns.forEach((txn) => {
          const dateAdjustedTxn = txn.set('postedOnAsUnix', convertToUnixDay(txn.postedOn));

          // add the transaction to the reduction list
          const oldTxn = flatTxnByIdList.get(txn.id);
          if (doOptimize) newState = addReduction(newState, oldTxn, txn);

          if (!dateAdjustedTxn.isDeleted) {
            if (replace) {
              txnsById.set(dateAdjustedTxn.id, dateAdjustedTxn);
            } else {
              // not aware of anywhere this is used (replace = false)
              // txnsById.mergeDeepIn([dateAdjustedTxn.id], dateAdjustedTxn);
            }
          } else {
            txnsById.delete(txn.id);
          }
        });
      });
      if (txnsByIdForAccountId.size > 0) {
        txnsByAccountId.set(accountId, txnsByIdForAccountId);
      } else {
        txnsByAccountId.delete(accountId);
      }
    });
  });

  return newState.merge({
    transactionsByAccountId,
    loadPending: false,
    lastSyncDate: lastSyncDate || newState.lastSyncDate,
  });
}

export function convertToUnixDay(postedOn) {
  return moment(moment(postedOn).format('YYYY-MM-DD')).unix();
}

export function setupTxnsUndo(state, action) {
  if (action && action.meta && action.meta.undo && action.meta.undo.userMessage && action.payload && action.payload.resources) {
    const createTransactions = [];
    const updateTransactions = [];
    const deleteTransactions = [];
    action.payload.resources.forEach((transaction) => {
      if (transaction.id !== transaction.clientId) {
        const existingResource = findTransaction(state.transactionsByAccountId, transaction.id, transaction.accountId);
        if (transaction.isDeleted && existingResource) { // action=delete => undo=create
          let resource = existingResource.delete('id');
          resource = resource.set('clientId', uuidv4().toUpperCase());
          createTransactions.push(resource);
        } else if (!existingResource) { // action=create => undo=delete
          const resource = transaction.set('isDeleted', true);
          deleteTransactions.push(resource);
        } else { // action= update => undo=update
          updateTransactions.push(existingResource);
        }
      }
    });

    const { undo, ...meta } = action.meta || {};

    const createTransactionsAction = createTransactions.length > 0 && transactionActions.createTransactions(createTransactions, meta);
    const updateTransactionsAction = updateTransactions.length > 0 && transactionActions.updateTransactions(updateTransactions, meta);
    const deleteTransactionsAction = deleteTransactions.length > 0 && transactionActions.deleteTransactions(deleteTransactions, meta);
    const actions = [
      ...(createTransactionsAction ? [createTransactionsAction] : []),
      ...(updateTransactionsAction ? [updateTransactionsAction] : []),
      ...(deleteTransactionsAction ? [deleteTransactionsAction] : []),
    ];
    if (actions && actions.length) {
      const undoAction = mkUndo({
        actions,
        userMessage: action.meta.undo.userMessage,
        key: uuidv4(),
      });
      action.asyncDispatch(undoActions.addUndo(undoAction));
    }
  }
}
